<script setup lang="ts">
import type {
  Attachment,
  ExternalCourse,
  ExternalCourseLevel,
  FineUploaderUpload,
  Institution,
} from '@/models/current-student-advanced-standing-form/api'
import type { AlertTone } from '@/models/current-student-advanced-standing-form/modalAlert'
import CrudModalShell from '@/components/current-student-advanced-standing-form/shared/CrudModalShell.vue'
import ExternalCourseAttachments from '@/components/current-student-advanced-standing-form/modals/external-course/ExternalCourseAttachments.vue'
import ExternalCourseCourseDetailsFieldset from '@/components/current-student-advanced-standing-form/modals/external-course/ExternalCourseCourseDetailsFieldset.vue'
import ExternalCourseInstitutionDetailsFieldset from '@/components/current-student-advanced-standing-form/modals/external-course/ExternalCourseInstitutionDetailsFieldset.vue'
import ModalAlert from '@/components/current-student-advanced-standing-form/shared/ModalAlert.vue'

defineProps<{
  shown: boolean
  active: boolean
  institutionSearchText: string
  institutionSuggestions: Institution[]
  institutionActiveIndex: number
  externalCourseLevelId: string
  courseLevelList: ExternalCourseLevel[]
  externalCourseName: string
  externalCourseCode: string
  externalCourseCompletedYear: string
  currentExternalCourse: ExternalCourse | null
  courseAttachmentDeleteIds: number[]
  showCourseFineUploader: boolean
  visibleCourseNewAttachments: FineUploaderUpload[]
  courseUploadDropActive: boolean
  courseUploadAlertMessage: string
  errorMessage: string
  alertTone: AlertTone
  saving: boolean
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const emit = defineEmits<{
  'update:institutionSearchText': [value: string]
  'update:externalCourseLevelId': [value: string]
  'update:externalCourseName': [value: string]
  'update:externalCourseCode': [value: string]
  'update:externalCourseCompletedYear': [value: string]
  useCustomInstitution: []
  institutionSearchInput: []
  institutionSearchKeydown: [event: KeyboardEvent]
  institutionSearchBlur: []
  activateInstitution: [index: number]
  selectInstitution: [institution: Institution]
  stageCourseAttachmentDelete: [attachment: Attachment]
  courseUploadDragEnter: [event: DragEvent]
  courseUploadDragOver: [event: DragEvent]
  courseUploadDragLeave: [event: DragEvent]
  courseUploadDrop: [event: DragEvent]
  triggerUploadInput: [event: MouseEvent]
  uploadButtonKeydown: [event: KeyboardEvent]
  uploadButtonHover: [event: MouseEvent, hover: boolean]
  uploadButtonFocus: [event: FocusEvent, focused: boolean]
  courseAttachmentSelected: [event: Event]
  cancelStagedUpload: [attachment: FineUploaderUpload]
  retryCourseAttachment: [attachment: FineUploaderUpload]
  deleteStagedCourseAttachment: [attachment: FineUploaderUpload]
  closeCourseUploadAlert: []
  delete: []
  close: []
  save: []
}>()

</script>

<template>
  <CrudModalShell
    modal-id="ExternalCourseModal"
    title="Add Tertiary Course"
    :shown="shown"
    :active="active"
    delete-button-id="ExternalCourseModalDeleteBtn"
    :delete-hidden="currentExternalCourse == null"
    save-button-id="ExternalCourseModalSaveBtn"
    :saving="saving"
    cancel-button-class="btn btn-secondary"
    @close="emit('close')"
    @delete="emit('delete')"
    @save="emit('save')"
  >
          <ExternalCourseInstitutionDetailsFieldset
            :institution-search-text="institutionSearchText"
            :institution-suggestions="institutionSuggestions"
            :institution-active-index="institutionActiveIndex"
            @update:institution-search-text="emit('update:institutionSearchText', $event)"
            @use-custom-institution="emit('useCustomInstitution')"
            @institution-search-input="emit('institutionSearchInput')"
            @institution-search-keydown="emit('institutionSearchKeydown', $event)"
            @institution-search-blur="emit('institutionSearchBlur')"
            @activate-institution="emit('activateInstitution', $event)"
            @select-institution="emit('selectInstitution', $event)"
          />

          <ExternalCourseCourseDetailsFieldset
            :external-course-level-id="externalCourseLevelId"
            :course-level-list="courseLevelList"
            :external-course-name="externalCourseName"
            :external-course-code="externalCourseCode"
            :external-course-completed-year="externalCourseCompletedYear"
            @update:external-course-level-id="emit('update:externalCourseLevelId', $event)"
            @update:external-course-name="emit('update:externalCourseName', $event)"
            @update:external-course-code="emit('update:externalCourseCode', $event)"
            @update:external-course-completed-year="emit('update:externalCourseCompletedYear', $event)"
          >
            <ExternalCourseAttachments
              :saved-attachments="currentExternalCourse?.external_course_attachments ?? []"
              :course-attachment-delete-ids="courseAttachmentDeleteIds"
              :show-course-fine-uploader="showCourseFineUploader"
              :visible-course-new-attachments="visibleCourseNewAttachments"
              :course-upload-drop-active="courseUploadDropActive"
              :course-upload-alert-message="courseUploadAlertMessage"
              :upload-thumbnail-url="uploadThumbnailUrl"
              @stage-course-attachment-delete="emit('stageCourseAttachmentDelete', $event)"
              @course-upload-drag-enter="emit('courseUploadDragEnter', $event)"
              @course-upload-drag-over="emit('courseUploadDragOver', $event)"
              @course-upload-drag-leave="emit('courseUploadDragLeave', $event)"
              @course-upload-drop="emit('courseUploadDrop', $event)"
              @trigger-upload-input="emit('triggerUploadInput', $event)"
              @upload-button-keydown="emit('uploadButtonKeydown', $event)"
              @upload-button-hover="(event, hover) => emit('uploadButtonHover', event, hover)"
              @upload-button-focus="(event, focused) => emit('uploadButtonFocus', event, focused)"
              @course-attachment-selected="emit('courseAttachmentSelected', $event)"
              @cancel-staged-upload="emit('cancelStagedUpload', $event)"
              @retry-course-attachment="emit('retryCourseAttachment', $event)"
              @delete-staged-course-attachment="emit('deleteStagedCourseAttachment', $event)"
              @close-course-upload-alert="emit('closeCourseUploadAlert')"
            />
          </ExternalCourseCourseDetailsFieldset>
          <ModalAlert alert-id="externalCourseModalError" :alert-tone="alertTone" :message="errorMessage" />
  </CrudModalShell>
</template>
